import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, TrendingUp } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { api, money, prettyDate } from "./api";

const STATUS_STYLES = {
  pending: "border-yellow-500/40 bg-yellow-400/10 text-yellow-700",
  approved: "border-sky-500/40 bg-sky-400/10 text-sky-700",
  paid: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
};

export default function PortalSettlements() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/portal/settlements").then(({ data }) => setRows(data)).catch(() => toast.error("Could not load settlements"));
  }, []);

  return (
    <PortalShell title="Settlement Reports">
      <p className="mb-6 max-w-2xl text-sm leading-relaxed text-muted">
        Weekly settlements are calculated from Eligible Rental Hours: 40% partner share from 0–15 hours, and 60% on all
        eligible revenue once the week reaches 16+ hours. Payouts are issued Wednesdays after XE approval.
      </p>
      {rows === null ? (
        <p className="py-16 text-center text-sm text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="card-line rounded-xl p-12 text-center" data-testid="settlements-empty">
          <Landmark size={22} className="mx-auto text-aqua" />
          <p className="mt-4 text-sm text-muted">No settlements yet. Your first weekly settlement appears after XE generates it.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="settlements-list">
          {rows.map((s) => (
            <div key={s.settlement_id} className="card-line rounded-xl p-5" data-testid={`settlement-${s.settlement_id}`}>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="font-mono text-xs text-aqua">{s.settlement_id}</span>
                <span className="text-sm font-semibold text-offwhite">{prettyDate(s.week_start)} – {prettyDate(s.week_end)}</span>
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[s.status]}`}>{s.status}</span>
                <span className="ml-auto text-xs text-muted">Payout: {prettyDate(s.payout_date)}</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Eligible Hours</p><p className="mt-1 font-display text-lg font-bold text-offwhite">{s.eligible_hours}</p></div>
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Gross Revenue</p><p className="mt-1 font-display text-lg font-bold text-offwhite">{money(s.gross_revenue)}</p></div>
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Your Share</p><p className="mt-1 flex items-center gap-1 font-display text-lg font-bold text-aqua"><TrendingUp size={14} />{s.partner_pct}%</p></div>
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Your Payout</p><p className="mt-1 font-display text-lg font-bold text-aqua">{money(s.partner_amount)}</p></div>
              </div>
              {s.adjustments?.length > 0 && (
                <div className="mt-4 border-t border-black/[0.08] pt-3">
                  <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-muted">Adjustments</p>
                  {s.adjustments.map((a, i) => (
                    <p key={i} className="text-xs text-muted">{money(a.amount)} — {a.reason}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PortalShell>
  );
}
